import { useCallback } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import type { Call } from "@vapi-ai/server-sdk/api";

type NavigationOptions = {
  replaceSameCall?: boolean;
};

export const useCallNavigation = (botId: string | null) => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const callIdFromParams = searchParams.get('bot_call_id');
  const onlyCall = searchParams.get('only_call') === 'true';

  // Navigate to a call - updates the URL with the call ID
  const navigateToCall = useCallback((call: Call | null, options: NavigationOptions = {}) => {
    if (!botId) return;
    
    const { replaceSameCall = true } = options;
    const newParams = new URLSearchParams(searchParams);
    
    if (call) {
      // Only update if different call or forced replace
      if (callIdFromParams !== call.id || replaceSameCall) {
        newParams.set('bot_call_id', call.id);
        navigate(`/bot-portal/${botId}?${newParams.toString()}`, { replace: replaceSameCall });
      }
    } else {
      // Remove call ID from params if call is null
      newParams.delete('bot_call_id');
      navigate(`/bot-portal/${botId}?${newParams.toString()}`, { replace: true });
    }
  }, [botId, callIdFromParams, navigate, searchParams]);
  
  // Clear the selected call from the URL
  const clearSelectedCall = useCallback(() => {
    if (!botId) return;
    
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('bot_call_id');
    navigate(`/bot-portal/${botId}?${newParams.toString()}`, { replace: true });
  }, [botId, navigate, searchParams]);
  
  // Navigate to place call tab
  const navigateToPlaceCallTab = useCallback((keepCallId: boolean = false) => {
    if (!botId) return;
    
    const newParams = new URLSearchParams(searchParams);
    newParams.set('only_call', 'true');
    
    if (!keepCallId) {
      newParams.delete('bot_call_id');
    }
    
    navigate(`/bot-portal/${botId}?${newParams.toString()}`);
  }, [botId, navigate, searchParams]);
  
  // Navigate to calls tab
  const navigateToCallsTab = useCallback((keepCallId: boolean = true) => {
    if (!botId) return;
    
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('only_call');
    
    if (!keepCallId) {
      newParams.delete('bot_call_id');
    }
    
    navigate(`/bot-portal/${botId}?${newParams.toString()}`);
  }, [botId, navigate, searchParams]);
  
  // Find the call that matches the URL parameter
  const findCallFromParams = useCallback((allCalls: Call[]): Call | null => {
    if (!callIdFromParams) return null;
    return allCalls.find(call => call.id === callIdFromParams) || null;
  }, [callIdFromParams]);

  return {
    callIdFromParams,
    onlyCall,
    navigateToCall,
    clearSelectedCall,
    navigateToPlaceCallTab,
    navigateToCallsTab,
    findCallFromParams
  };
};
